import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { useSeo } from "@/hooks/useSeo";
import { getContentById, getRelated } from "@/lib/content";
import { getCategoryMeta } from "@/mocks/categories";
import ShareBar from "./components/ShareBar";
import RelatedList from "./components/RelatedList";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getContentById(id) : undefined;
  const meta = item ? getCategoryMeta(item.category) : undefined;

  useSeo({
    title: item ? `${item.title} - 红桃网` : "内容未找到 - 红桃网",
    description: item
      ? item.summary
      : "抱歉，未找到该内容，请返回首页浏览更多影视资讯与娱乐内容。",
    keywords: item
      ? `${item.title},${item.genre ?? ""},${meta?.name ?? ""},红桃网`
      : "红桃网",
  });

  if (!item || !meta) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 w-full flex items-center justify-center">
          <EmptyState
            message="抱歉，未找到对应的内容，它可能已被移除或链接有误。"
            actionLabel="返回首页"
            actionTo="/"
          />
        </main>
        <Footer />
      </div>
    );
  }

  const related = getRelated(item, 4);

  // 图文混排：在第一个小标题前插入一张配图
  const splitIndex = item.content.indexOf("<h3>");
  const before = splitIndex >= 0 ? item.content.slice(0, splitIndex) : item.content;
  const after = splitIndex >= 0 ? item.content.slice(splitIndex) : "";
  const inlineImage =
    "https://readdy.ai/api/search-image?query=cinematic%20warm%20toned%20film%20still%20with%20soft%20light%20and%20red%20amber%20accents%2C%20artistic%20atmosphere%2C%20editorial%20photography&width=1200&height=675&seq=detail-inline&orientation=landscape";

  const extraMeta = [
    item.year && `年份：${item.year}`,
    item.genre && `类型：${item.genre}`,
    item.region && `地区：${item.region}`,
    item.latestEpisode && item.latestEpisode,
  ].filter(Boolean) as string[];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 w-full">
        <div className="w-full px-4 md:px-8 py-5">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: meta.name, to: meta.path },
              { label: item.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-8 pb-10">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={meta.path}
                className="px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium hover:bg-primary-100 transition-colors cursor-pointer"
              >
                {meta.name}
              </Link>
              {extraMeta.map((m) => (
                <span
                  key={m}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs"
                >
                  {m}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-snug mb-4">
              {item.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500 pb-5 mb-6 border-b border-background-200 flex-wrap">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" /> {item.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" /> {item.date}
              </span>
              <span className="flex items-center gap-1.5 text-accent-600">
                <i className="ri-fire-line" /> 热度 {item.heat}
              </span>
            </div>

            <div className="w-full aspect-[16/9] rounded-xl overflow-hidden mb-7">
              <img
                src={item.cover}
                alt={item.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="article-content">
              <p className="text-base md:text-lg text-foreground-800 font-medium leading-relaxed mb-4">
                {item.summary}
              </p>
              <div dangerouslySetInnerHTML={{ __html: before }} />
              {after && (
                <>
                  <img src={inlineImage} alt={item.title} />
                  <div dangerouslySetInnerHTML={{ __html: after }} />
                </>
              )}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200">
              <ShareBar title={item.title} />
            </div>

            <div className="mt-8 rounded-xl bg-background-100 border border-background-200 p-5 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className={`${meta.icon} text-2xl text-primary-600`} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">
                    更多{meta.name}
                  </p>
                  <p className="text-xs text-foreground-500">
                    前往栏目查看全部内容
                  </p>
                </div>
              </div>
              <Link
                to={meta.path}
                className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                进入{meta.name}
              </Link>
            </div>

            <div className="mt-10">
              <RelatedList items={related} />
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}